import React, { useState } from 'react';
import Layout from '../components/Layout';
import { ChevronDown, HelpCircle } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'Comment choisissons-nous les bookmakers pour l\'analyse?',
      answer:
        'Nos experts évaluent chaque bookmaker selon des critères rigoureux, incluant la licence, la sécurité, la variété de sports, les bonus offerts, les méthodes de paiement, les délais de retrait et les retours des utilisateurs. Seuls les bookmakers qui répondent à nos standards sont recommandés.',
    },
    {
      question: 'Les bookmakers sont-ils sûrs et licenciés?',
      answer:
        'Oui, tous les bookmakers que nous analysons possèdent des licences valides délivrées par des autorités régulatrices reconnues, comme l\'ANJ (Autorité Nationale des Jeux) en France, la Commission de Gibraltar ou la Malta Gaming Authority. Nous vérifions régulièrement le statut des licences de chaque bookmaker.',
    },
    {
      question: 'Comment fonctionnent les bonus de bienvenue?',
      answer:
        'Les bonus de bienvenue sont des offres spéciales pour les nouveaux parieurs qui effectuent leur premier dépôt. Généralement, le bookmaker correspond à un pourcentage du montant déposé jusqu\'à une limite maximale. Par exemple, un bonus de 100% jusqu\'à 100€ signifie que si vous déposez 100€, vous recevrez 100€ supplémentaires pour parier.',
    },
    {
      question: 'Y a-t-il des frais pour utiliser les services de Primorixx?',
      answer:
        'Non, nos services sont entièrement gratuits pour les utilisateurs. Nous ne facturons aucun frais pour utiliser notre site, lire nos analyses ou comparer les offres.',
    },
    {
      question: 'Comment puis-je faire confiance à vos analyses?',
      answer:
        'Nos analyses sont basées sur des tests réels, l\'expérience des utilisateurs et des critères objectifs. Nous testons chaque bookmaker personnellement, analysons les termes et conditions, et vérifions tous les aspects importants. Nous maintenons une transparence totale sur nos méthodes et critères d\'évaluation.',
    },
    {
      question: 'Recevez-vous une commission des bookmakers?',
      answer:
        'Certains bookmakers peuvent nous payer une commission lorsqu\'un utilisateur s\'inscrit via nos liens. Cependant, cela n\'influence jamais nos évaluations ou classements. Nous évaluons chaque bookmaker de manière impartiale, indépendamment de toute relation commerciale.',
    },
    {
      question: 'À quelle fréquence les informations sont-elles mises à jour?',
      answer:
        'Nous mettons à jour nos analyses régulièrement, surtout lorsqu\'il y a des changements importants dans les bonus, termes ou conditions. Nous vérifions les sites des bookmakers hebdomadairement pour garantir que les informations soient toujours à jour.',
    },
    {
      question: 'Puis-je parier en France en utilisant ces bookmakers?',
      answer:
        'Nous recommandons uniquement les bookmakers qui acceptent les parieurs français et sont conformes aux réglementations applicables. Cependant, il est de la responsabilité de chaque parieur de vérifier les lois locales sur les paris en ligne avant de s\'inscrire.',
    },
    {
      question: 'Comment puis-je réclamer ou signaler un problème?',
      answer:
        'Si vous avez une expérience négative avec un bookmaker que nous recommandons ou si vous trouvez des informations obsolètes sur notre site, contactez-nous via notre page de contact. Nous prenons toutes les réclamations au sérieux et enquêtons sur chaque cas.',
    },
    {
      question: 'Offrez-vous un support client?',
      answer:
        'Bien que nous ne soyons pas un bookmaker et que nous n\'offrions pas de support direct pour les questions liées aux paris ou aux paiements, nous sommes toujours disponibles pour aider avec les questions sur notre site, analyses ou comparaisons. Contactez-nous et nous répondrons le plus rapidement possible.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Layout
      title="Questions Fréquentes"
      description="Trouvez des réponses aux questions les plus fréquentes sur les sites de paris sportifs, les bonus, la sécurité et bien plus encore sur Primorixx."
      canonicalUrl="/faq"
      jsonLd={jsonLd}
    >
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-dark-accent/20 rounded-full mb-6">
                <HelpCircle className="w-8 h-8 text-dark-accent" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
                Questions Fréquentes
              </h1>
              <p className="text-xl text-dark-muted">
                Trouvez des réponses à vos questions sur les sites de paris sportifs,
                les bonus, la sécurité et bien plus encore
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-dark-card border border-dark-border rounded-xl overflow-hidden transition-all duration-300"
                  >
                    <button
                      onClick={() =>
                        setOpenIndex(openIndex === index ? null : index)
                      }
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-dark-surface/50 transition-colors"
                    >
                      <span className="text-lg font-semibold text-white pr-4">
                        {faq.question}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-dark-accent flex-shrink-0 transition-transform duration-300 ${
                          openIndex === index ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openIndex === index && (
                      <div className="px-6 pb-5 animate-slide-up">
                        <p className="text-dark-muted leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center bg-dark-card border border-dark-border rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-4 gradient-text">
                Vous N'avez Pas Trouvé la Réponse?
              </h2>
              <p className="text-dark-muted mb-6">
                Si vous avez encore des questions, n'hésitez pas à nous contacter.
                Nous sommes là pour vous aider!
              </p>
              <a
                href="mailto:contact@primorixx.com"
                className="inline-flex items-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
              >
                Nous Contacter
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FAQ;

